import React, { ReactNode, useCallback, useState } from "react";
import styled from "styled-components";
import { pick } from "lodash";
import { Editor, EditorRenderProps } from "@sonder/editor";
import { PrimaryButton } from "@sonder/ui/source/styled/fragments/PrimaryButton";
import {
  CommentActions,
  CommentLayout,
} from "@sonder/ui/source/styled/layouts/CommentLayout";
import { useMultiKeyPress } from "core/hooks/useMultiKeyPress";
import { UserMentionSearch } from "tasks/components/TaskPanel/Activity/UserMentionSearch";

const CommentEditor = styled(Editor)`
  margin: 0.75rem 0;
  max-height: 10rem;
  min-height: 2rem;
  min-width: 5rem;
  overflow-y: scroll;
  padding: 0 1.5rem;
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  margin-left: auto;
`;

type EditorContentProps = Pick<
  EditorRenderProps,
  "htmlBody" | "plainBody" | "mentions"
>;

interface CommentInputProps {
  children: ReactNode;
  disabled?: boolean;
  submitting?: boolean;
  onPostComment?(contentProps: EditorContentProps): Promise<void> | void;
}
export const CommentInput = ({
  children,
  disabled = false,
  submitting = false,
  onPostComment = () => undefined,
}: CommentInputProps) => {
  const [editorContent, setEditorContent] = useState<EditorContentProps | null>(
    null,
  );
  const [clearEditorContentFlag, setClearEditorContentFlag] = useState(false);

  const handleCommentSubmit = useCallback(async () => {
    if (editorContent?.plainBody) {
      setEditorContent(null);
      setClearEditorContentFlag(true);
      await onPostComment(editorContent);
    }
  }, [editorContent]);

  useMultiKeyPress(["Meta", "Enter"], handleCommentSubmit);

  return (
    <CommentLayout>
      <CommentEditor
        disabled={disabled || submitting}
        htmlBody={editorContent?.htmlBody}
        placeholder="Comment on thread"
        plainBody={editorContent?.plainBody}
      >
        {renderProps => {
          // !TODO: add an onChange handler for this sort of thing
          if (renderProps.plainBody !== editorContent?.plainBody)
            setEditorContent(
              pick(renderProps, ["htmlBody", "plainBody", "mentions"]),
            );

          if (clearEditorContentFlag) {
            renderProps.clearEditorState();
            setClearEditorContentFlag(false);
          }
          return <UserMentionSearch {...renderProps} />;
        }}
      </CommentEditor>
      <CommentActions>
        {children}
        <StyledPrimaryButton
          disabled={disabled || !editorContent || submitting}
          onClick={handleCommentSubmit}
        >
          {submitting ? "Sending..." : "Send"}
        </StyledPrimaryButton>
      </CommentActions>
    </CommentLayout>
  );
};
